import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import {
  clampSidecarWidth,
  parseSidecarTab,
  readSidecarPreference,
  resolveTracks,
  writeSidecarPreference,
  type SidecarTab,
} from './sidecar'
import { useAvailableWidth } from './use-available-width'

interface SidecarSearch {
  readonly sidecar?: string
}

/**
 * Sidecar 的开合、页签、宽度与三条轨道的解算。
 *
 * # 状态分两处存，这是刻意的
 *
 * 页签在 URL query 里（`?sidecar=board`），宽度在 localStorage 里。
 * 理由写在 `sidecar.ts`：页签属于这一次导航（该能分享、该能后退），
 * 宽度属于这个人（换一条会话也该保持）。
 *
 * # 为什么用 `?sidecar=` 而不是一个 useState
 *
 * 设计稿要求刷新之后 Sidecar 还开着。更重要的是：这条轨道的开合**不允许**
 * 卸载聊天区。放进路由 search 之后，开合只是同一棵组件树的一次重渲染。
 *
 * # 为什么抽出来
 *
 * 2026-09-08 之前只有 /lulu 有它，项目页里的对话把 `onOpenSidecar` 写成
 * 空函数——「团队」那颗按钮点了没反应，没有团队面板、审批、看板。
 * 主屏与项目页共用这一份之后，两处的 Sidecar 才是同一个东西。
 */
export function useSidecarLayout() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as SidecarSearch
  const [preference, setPreference] = useState(() => readSidecarPreference())
  useEffect(() => {
    writeSidecarPreference(preference)
  }, [preference])

  // 量的是外面这层容器，不是窗口。见 use-available-width.ts。
  const [measureRef, availableWidth] = useAvailableWidth()
  const sidecarTab = parseSidecarTab(search.sidecar)
  const tracks = resolveTracks(availableWidth, sidecarTab !== null, preference.width, false)

  const openSidecar = useCallback(
    (tab: SidecarTab) => {
      void navigate({ to: '.', search: (prev: SidecarSearch) => ({ ...prev, sidecar: tab }) })
    },
    [navigate],
  )
  const closeSidecar = useCallback(() => {
    void navigate({
      to: '.',
      search: (prev: SidecarSearch) => {
        const next = { ...prev }
        delete next.sidecar
        return next
      },
    })
  }, [navigate])
  const resizeSidecar = useCallback((width: number) => {
    setPreference((prev) => ({ ...prev, width: clampSidecarWidth(width) }))
  }, [])

  return { measureRef, tracks, sidecarTab, openSidecar, closeSidecar, resizeSidecar }
}
